import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { ChevronDown, ChevronUp, Filter } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SelectField from './SelectField.js';
import { filterGridClassName, filterPanelClass } from './uiClasses.js';

export const FILTER_ROW_LIMIT = 5;
export const MOBILE_FILTER_LIMIT = 4;

export { filterGridClassName };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia('(max-width: 767px)').matches
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 767px)');
    const handler = (event) => setIsMobile(event.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  return isMobile;
}

const upgradeSelectToSelectField = (node) => {
  if (!React.isValidElement(node)) return node;

  if (node.type === 'select') {
    const { value, onChange, name, disabled, children } = node.props;
    return (
      <SelectField
        variant="filter"
        value={value ?? ''}
        onChange={onChange}
        name={name}
        disabled={disabled}
      >
        {children}
      </SelectField>
    );
  }

  if (node.props?.children) {
    const upgradedChildren = React.Children.map(node.props.children, upgradeSelectToSelectField);
    if (upgradedChildren !== node.props.children) {
      return React.cloneElement(node, {}, upgradedChildren);
    }
  }

  return node;
};

/**
 * Reusable filter panel for list pages.
 * - Mobile: 4 filters in a 2x2 grid; chevron for filters beyond 4
 * - Tablet/desktop: up to 5 filters visible; chevron when more than 5
 * - Native <select> children are auto-upgraded to SelectField for consistent dropdown UI
 */
const ListFilterPanel = ({
  children,
  hasActiveFilters = false,
  onClearFilters,
  title,
  gridClassName = filterGridClassName,
  className = '',
  mobileSingleColumn = false,
}) => {
  const { t } = useTranslation();
  const [showExtraFilters, setShowExtraFilters] = useState(false);
  const isMobile = useIsMobile();

  const childArray = useMemo(() => {
    return React.Children.toArray(children)
      .filter(Boolean)
      .map(upgradeSelectToSelectField);
  }, [children]);

  const primaryLimit = isMobile
    ? (mobileSingleColumn ? childArray.length : MOBILE_FILTER_LIMIT)
    : FILTER_ROW_LIMIT;
  const primaryFilters = childArray.slice(0, primaryLimit);
  const extraFilters = childArray.slice(primaryLimit);
  const hasExtraFilters = extraFilters.length > 0;
  const resolvedGridClass = mobileSingleColumn
    ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-2 md:gap-4'
    : gridClassName;
  const mobileFieldClass = mobileSingleColumn
    ? '[&>div]:relative [&>div]:min-w-0'
    : '[&>div]:relative [&>div]:min-w-0 max-md:[&_label.block]:hidden max-md:[&>div:has(label.flex)]:col-span-2 max-md:[&_button]:py-1.5 max-md:[&_input:not([type=checkbox])]:py-1.5';

  return (
    <div className={`${filterPanelClass} ${className}`.trim()}>
      <div className="flex items-center justify-between mb-3 sm:mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 sm:w-4 sm:h-4 text-gray-500 font-bold" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-700">
            {title || t('common.showFilters')}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          {hasExtraFilters && (
            <button
              type="button"
              onClick={() => setShowExtraFilters((prev) => !prev)}
              className="flex items-center gap-1 text-blue-500 hover:text-blue-600 transition-colors"
              title={
                showExtraFilters
                  ? t('common.showLessFilters', 'Show less filters')
                  : t('common.showMoreFilters', 'Show more filters')
              }
              aria-expanded={showExtraFilters}
            >
              {showExtraFilters ? (
                <ChevronUp className="h-5 w-5" />
              ) : (
                <ChevronDown className="h-5 w-5" />
              )}
              <span className="text-xs font-medium md:text-sm">
                {showExtraFilters ? t('common.less', 'Less') : t('common.more', 'More')}
              </span>
            </button>
          )}
          {hasActiveFilters && onClearFilters && (
            <button
              type="button"
              onClick={onClearFilters}
              className="flex items-center gap-1 text-gray-500 hover:text-gray-700 transition-colors"
              title={t('common.clearAllFilters')}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
              <span className="hidden md:inline text-sm font-medium">{t('common.clear')}</span>
            </button>
          )}
        </div>
      </div>

      <div
        className={`${resolvedGridClass} ${mobileFieldClass}`}
      >
        {primaryFilters}
      </div>

      {hasExtraFilters && showExtraFilters && (
        <div
          className={`${resolvedGridClass} mt-2 md:mt-4 pt-2 md:pt-4 border-t border-gray-200 ${mobileFieldClass}`}
        >
          {extraFilters}
        </div>
      )}
    </div>
  );
};

export default ListFilterPanel;
